import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import { useSesion } from '../auth/sesion';
import Liquidaciones from './Liquidaciones';
import {
  Aviso,
  Campo,
  Cargando,
  Casilla,
  Etiqueta,
  Seleccion,
  Tarjeta,
  Vacio,
  fecha,
  pesos,
} from '../ui/piezas';

/**
 * El mandato es el permiso del dueño para vender o arrendar.
 *
 * Vive dentro de la ficha del inmueble y no en una pantalla aparte porque es lo
 * que se quiere ver justo antes de publicar: si hay autorización, de qué tipo y
 * con qué comisión.
 */

interface Mandato {
  id: string;
  inmuebleId: string;
  propietarioId: string;
  propietario: string;
  sedeId: string;
  sede: string;
  tipo: 'VENTA' | 'ARRIENDO' | 'AMBOS';
  exclusividad: boolean;
  comisionPorcentaje: number | null;
  comisionAdministracionPorcentaje: number | null;
  comisionMinima: number | null;
  precioAutorizadoMin: number | null;
  firmadoEn: string | null;
  vigenteDesde: string | null;
  vigenteHasta: string | null;
  estado: 'BORRADOR' | 'VIGENTE' | 'VENCIDO' | 'TERMINADO';
  autorizaHoy: boolean;
  notas: string | null;
}

interface PropietarioBreve {
  id: string;
  nombre: string;
}

const TIPOS = [
  { valor: 'VENTA', texto: 'Venta' },
  { valor: 'ARRIENDO', texto: 'Arriendo' },
  { valor: 'AMBOS', texto: 'Venta y arriendo' },
];

/*
 * Solo los saltos que la API acepta desde cada estado. Ofrecer los demás sería
 * invitar a un 409, y quien lo pulse no tiene forma de saber por qué falló.
 */
const SIGUIENTES: Record<Mandato['estado'], Mandato['estado'][]> = {
  BORRADOR: ['VIGENTE', 'TERMINADO'],
  VIGENTE: ['VENCIDO', 'TERMINADO'],
  VENCIDO: ['VIGENTE', 'TERMINADO'],
  TERMINADO: [],
};

const ROTULO: Record<Mandato['estado'], string> = {
  BORRADOR: 'Volver a borrador',
  VIGENTE: 'Poner vigente',
  VENCIDO: 'Marcar vencido',
  TERMINADO: 'Terminar',
};

interface Borrador {
  propietarioId: string;
  tipo: string;
  exclusividad: boolean;
  comisionPorcentaje: string;
  comisionAdministracionPorcentaje: string;
  comisionMinima: string;
  precioAutorizadoMin: string;
  firmadoEn: string;
  vigenteDesde: string;
  vigenteHasta: string;
  notas: string;
}

const VACIO: Borrador = {
  propietarioId: '',
  tipo: 'VENTA',
  exclusividad: false,
  comisionPorcentaje: '',
  comisionAdministracionPorcentaje: '',
  comisionMinima: '',
  precioAutorizadoMin: '',
  firmadoEn: '',
  vigenteDesde: '',
  vigenteHasta: '',
  notas: '',
};

/** Un campo vacío es «no lo sé», no «cero». */
function numero(valor: string): number | null {
  const limpio = valor.trim();
  if (limpio === '') return null;
  const leido = Number(limpio);
  return Number.isFinite(leido) ? leido : null;
}

function texto(valor: string): string | null {
  const limpio = valor.trim();
  return limpio === '' ? null : limpio;
}

export default function Mandatos({
  inmuebleId,
  sedeId,
}: {
  inmuebleId: string;
  sedeId: string;
}) {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [forma, setForma] = useState<Borrador>(VACIO);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const listado = useQuery({
    queryKey: ['mandatos', inmuebleId],
    queryFn: () => api<Mandato[]>(`/admin/mandatos?inmueble=${inmuebleId}`),
    enabled: puede('mandato:leer'),
  });

  const propietarios = useQuery({
    queryKey: ['propietarios-breve'],
    queryFn: () => api<{ content: PropietarioBreve[] }>('/admin/propietarios?size=200'),
    enabled: abierto,
  });

  const refrescar = () => {
    void cliente.invalidateQueries({ queryKey: ['mandatos', inmuebleId] });
    // El requisito de mandato se comprueba al publicar: la ficha también cambia.
    void cliente.invalidateQueries({ queryKey: ['inmueble', inmuebleId] });
  };

  const crear = useMutation({
    mutationFn: () =>
      api<Mandato>('/admin/mandatos', {
        metodo: 'POST',
        cuerpo: {
          inmuebleId,
          sedeId,
          propietarioId: forma.propietarioId,
          tipo: forma.tipo,
          exclusividad: forma.exclusividad,
          comisionPorcentaje: numero(forma.comisionPorcentaje),
          comisionAdministracionPorcentaje: numero(forma.comisionAdministracionPorcentaje),
          comisionMinima: numero(forma.comisionMinima),
          precioAutorizadoMin: numero(forma.precioAutorizadoMin),
          firmadoEn: texto(forma.firmadoEn),
          vigenteDesde: texto(forma.vigenteDesde),
          vigenteHasta: texto(forma.vigenteHasta),
          notas: texto(forma.notas),
        },
      }),
    onSuccess: () => {
      setForma(VACIO);
      setAbierto(false);
      setError(null);
      setExito('Mandato creado en borrador. Ponlo vigente cuando tengas el papel firmado.');
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: ({ id, destino }: { id: string; destino: string }) =>
      api<Mandato>(`/admin/mandatos/${id}/estado/${destino}`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      setExito(null);
      refrescar();
    },
    onError: (e) => setError(e),
  });

  if (!puede('mandato:leer')) return null;

  const mandatos = listado.data ?? [];
  const puedeCrear = puede('inmueble:editar');
  const puedeAprobar = puede('mandato:aprobar');

  return (
    <Tarjeta titulo="Mandato del propietario">
      <p style={{ opacity: 0.75, fontSize: 14, marginTop: 0 }}>
        La autorización escrita para vender o arrendar. Nace en borrador; ponerlo
        vigente es lo que fija la comisión.
      </p>

      <Aviso error={error} exito={exito} />

      {listado.isPending ? (
        <Cargando />
      ) : mandatos.length === 0 ? (
        <Vacio>Este inmueble no tiene mandato registrado.</Vacio>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mandatos.map((m) => (
            <div
              key={m.id}
              style={{ border: '1px solid var(--linea)', borderRadius: 8, padding: 12 }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Etiqueta>{m.estado}</Etiqueta>
                <b>{TIPOS.find((t) => t.valor === m.tipo)?.texto ?? m.tipo}</b>
                {m.exclusividad && <small style={{ opacity: 0.7 }}>· en exclusiva</small>}
                {m.estado === 'VIGENTE' && !m.autorizaHoy && (
                  <small style={{ color: '#B77714' }}>· fuera de fechas</small>
                )}
              </div>

              <div style={{ marginTop: 8, fontSize: 14, opacity: 0.85 }}>
                <div>Propietario: {m.propietario}</div>
                {m.comisionPorcentaje != null && (
                  <div>Comisión de venta: {m.comisionPorcentaje}%</div>
                )}
                {m.comisionAdministracionPorcentaje != null && (
                  <div>Comisión de administración: {m.comisionAdministracionPorcentaje}%</div>
                )}
                {m.comisionMinima != null && <div>Comisión mínima: {pesos(m.comisionMinima)}</div>}
                {m.precioAutorizadoMin != null && (
                  <div>No cerrar por debajo de: {pesos(m.precioAutorizadoMin)}</div>
                )}
                <div>
                  Firmado {fecha(m.firmadoEn)} · vigencia {fecha(m.vigenteDesde)} a{' '}
                  {m.vigenteHasta ? fecha(m.vigenteHasta) : 'sin fecha de fin'}
                </div>
                {m.notas && <div style={{ marginTop: 6 }}>{m.notas}</div>}
              </div>

              {puedeAprobar && SIGUIENTES[m.estado].length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {SIGUIENTES[m.estado].map((destino) => (
                    <button
                      key={destino}
                      className="plano"
                      disabled={cambiarEstado.isPending}
                      onClick={() => cambiarEstado.mutate({ id: m.id, destino })}
                    >
                      {ROTULO[destino]}
                    </button>
                  ))}
                </div>
              )}

              {/* Solo donde hay canon que repartir: un mandato de venta no
                  genera liquidaciones mensuales, y uno en borrador todavia no
                  autoriza nada. */}
              {m.estado === 'VIGENTE' && m.tipo !== 'VENTA' && (
                <Liquidaciones mandatoId={m.id} inmuebleId={inmuebleId} />
              )}
            </div>
          ))}
        </div>
      )}

      {puedeCrear && (
        <>
          <div style={{ height: 14 }} />
          <button className="plano" style={{ paddingLeft: 0 }} onClick={() => setAbierto(!abierto)}>
            {abierto ? 'Cancelar' : 'Registrar un mandato'}
          </button>
        </>
      )}

      {abierto && (
        <div style={{ marginTop: 12 }}>
          <Seleccion
            etiqueta="Propietario"
            requerido
            valor={forma.propietarioId}
            alCambiar={(v) => setForma({ ...forma, propietarioId: v })}
            vacio="Elige un propietario"
            opciones={(propietarios.data?.content ?? []).map((p) => ({
              valor: p.id,
              texto: p.nombre,
            }))}
            ayuda="Si no está en la lista, créalo antes en Propietarios."
          />
          <div style={{ height: 12 }} />
          <Seleccion
            etiqueta="Tipo"
            valor={forma.tipo}
            alCambiar={(v) => setForma({ ...forma, tipo: v })}
            opciones={TIPOS}
          />
          <div style={{ height: 12 }} />
          <Casilla
            etiqueta="En exclusiva"
            ayuda="Nadie más puede comercializarlo mientras esté vigente."
            valor={forma.exclusividad}
            alCambiar={(v) => setForma({ ...forma, exclusividad: v })}
          />

          <div style={{ height: 12 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo
              etiqueta="Comisión de venta (%)"
              valor={forma.comisionPorcentaje}
              alCambiar={(v) => setForma({ ...forma, comisionPorcentaje: v })}
            />
            <Campo
              etiqueta="Comisión de administración (%)"
              ayuda="La mensual del arriendo."
              valor={forma.comisionAdministracionPorcentaje}
              alCambiar={(v) => setForma({ ...forma, comisionAdministracionPorcentaje: v })}
            />
            <Campo
              etiqueta="Comisión mínima"
              ayuda="En pesos. Se aplica si el porcentaje se queda corto."
              valor={forma.comisionMinima}
              alCambiar={(v) => setForma({ ...forma, comisionMinima: v })}
            />
            <Campo
              etiqueta="Precio mínimo autorizado"
              ayuda="Por debajo de esto, el asesor vuelve a hablar con el dueño."
              valor={forma.precioAutorizadoMin}
              alCambiar={(v) => setForma({ ...forma, precioAutorizadoMin: v })}
            />
          </div>

          <div style={{ height: 12 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Campo
              etiqueta="Firmado el"
              tipo="date"
              ayuda="Exigido para ponerlo vigente."
              valor={forma.firmadoEn}
              alCambiar={(v) => setForma({ ...forma, firmadoEn: v })}
            />
            <Campo
              etiqueta="Vigente desde"
              tipo="date"
              valor={forma.vigenteDesde}
              alCambiar={(v) => setForma({ ...forma, vigenteDesde: v })}
            />
            <Campo
              etiqueta="Vigente hasta"
              tipo="date"
              ayuda="Vacío, sin fecha de fin."
              valor={forma.vigenteHasta}
              alCambiar={(v) => setForma({ ...forma, vigenteHasta: v })}
            />
          </div>

          <div style={{ height: 12 }} />
          <Campo
            etiqueta="Notas"
            valor={forma.notas}
            alCambiar={(v) => setForma({ ...forma, notas: v })}
          />

          <div style={{ height: 14 }} />
          <button disabled={!forma.propietarioId || crear.isPending} onClick={() => crear.mutate()}>
            {crear.isPending ? 'Guardando…' : 'Guardar mandato'}
          </button>
        </div>
      )}
    </Tarjeta>
  );
}
